import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import type { Period } from 'lumen/models';

import styles from './td-date-month.module.css';

interface HotelPricingAssistantTableCellsTdDateMonthSignature {
  Args: {
    column?: { onDayHeaderClick?: (period: Period) => void };
    value: Period;
  };
}

export default class HotelPricingAssistantTableCellsTdDateMonth extends Component<HotelPricingAssistantTableCellsTdDateMonthSignature> {
  @service declare access: Services['access'];
  @service declare dates: Services['dates'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];

  get formattedDate(): string {
    return this.args.value.fromDate.format(
      this.dates.partialDateFormat.WEEKDAY_AND_MONTH_AND_DAY_SHORT,
    );
  }

  get isPast(): boolean {
    return this.hotelAndMarketService.isPast(this.args.value.fromDate);
  }

  get isToday(): boolean {
    return this.args.value.fromDate.isSame(
      this.hotelAndMarketService.getLocalTime(),
      'day',
    );
  }

  get showDayOverrideModal(): boolean {
    if (this.isPast) {
      return false;
    }

    if (
      !(
        this.access.hasPricingAssistantRestrictionsEditRights ||
        this.access.isStaffUser
      )
    ) {
      return false;
    }

    return (
      this.access.hasPricingAssistantOverbookingRestrictionsFeature ||
      this.access.hasPricingAssistantMinLosRestrictionsFeature
    );
  }

  @action
  onClick(): void {
    if (!this.showDayOverrideModal) {
      return;
    }

    this.args.column?.onDayHeaderClick?.(this.args.value);
  }

  <template>
    {{#if this.showDayOverrideModal}}
      <button
        aria-haspopup="dialog"
        class="{{styles.date-cell-button}} cursor-pointer"
        type="button"
        {{on "click" this.onClick}}
      >
        <span
          aria-current={{if this.isToday "date"}}
          class="{{if this.isToday 'text-300--bold' 'text-300--medium'}}
            {{if this.isToday styles.today-date}}"
          data-test-id="date-month-cell"
        >
          {{this.formattedDate}}
        </span>
      </button>
    {{else}}
      <div class={{styles.date-cell-button}}>
        <span
          aria-current={{if this.isToday "date"}}
          class="{{if this.isToday 'text-300--bold' 'text-300--medium'}}
            {{if this.isPast 'text-disabled'}}
            {{if this.isToday styles.today-date}}"
          data-test-id="date-month-cell"
        >
          {{this.formattedDate}}
        </span>
      </div>
    {{/if}}
  </template>
}
